import { ApiError } from "@/api/client";
import {
  adoptionPreflightRefusalFromError,
  currentAdoptionsVersionFromError,
  useActivateProjectBackgroundMutation,
  useActivateProjectClosingConversationMutation,
  useActivateProjectMainConcernMutation,
  useActivateProjectPathwaysMutation,
  useAdoptionPreflightQuery,
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  useProjectAdoptionsQuery,
  useReplaceProjectAdoptionsMutation,
  type LibraryAsset,
} from "@/api/library";
import {
  activationImpact,
  adoptionImpact,
  adoptionLane,
  adoptionPolicyReady,
  replacementPlan,
  singlePinSentence,
  type AdoptionDecision,
  type ReplacementPlan,
} from "@/lib/adoptions";
import { PLACEMENT_REFUSAL_LABEL } from "@/lib/labels";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";
import { ErrorState, LoadingState } from "@/ui/states";

/**
 * The confirmation for one adoption decision, with its impact stated before the
 * write (#372).
 *
 * WHAT IT WRITES. `PUT /v1/projects/{projectId}/adoptions` replaces the WHOLE
 * pin set, so this sends the set it read back with exactly one entry changed,
 * under the `expectedAdoptionsVersion` it LOADED — never one re-read at submit
 * time, which would turn the concurrency check into a formality that always
 * passes. Both the sentence below and the array sent come out of one
 * `adoptionImpact` call, so a surface cannot describe one write and perform
 * another.
 *
 * ONE READ, NOT TWO. This reads the pin set through the same hook and therefore
 * the same cache entry as the list that opened it, which is a deliberate
 * departure from `MatrixPinning`: that dialog re-reads a DIFFERENT resource
 * (the asset-centric adoption list decided the action, the project's own set
 * built the write), and the gap between them is a race it has to detect and
 * refuse. There is no such gap here, so there is no such refusal — the identity
 * on screen, the impact described and the identity sent are one value.
 *
 * FAIL CLOSED. The confirm stays disabled until the pin set AND the catalog
 * that names those pins have both loaded, the same rule `LifecycleDialog` and
 * `MatrixPinning` apply: a replacement decided without seeing what it replaces
 * is not a decision, and without the catalog the surface cannot say WHICH pin a
 * confirmation would displace.
 *
 * AND THE SURFACES THAT OFFER THE CONTROL READ THAT RULE FROM HERE. This
 * dialog's own gate is `adoptionDecisionReady` below, exported so a caller can
 * ask the same question BEFORE rendering a control that would open it. A
 * surface with its own, weaker gate is the defect that produced this export:
 * `SectionReview` withheld its row actions only on a TERMINAL error, so a
 * retryable 503 landing on a refetch — which leaves `.data` in place while the
 * result becomes `status: "error"` — kept the actions on screen and opened this
 * dialog straight onto the load error with its confirmation disabled. A control
 * whose single outcome is that refusal is the affordance ADR 0001 forbids
 * (Codex, #570 round 2). One rule, one place, read by both halves.
 */

/**
 * The minimal shape of one settled read a decision depends on.
 *
 * Structural rather than `UseQueryResult<T>`, so the predicate is callable with
 * either query without carrying the union's full generic surface into a caller
 * that only has to answer yes or no.
 */
type DecisionRead = { readonly isSuccess: boolean };

/**
 * Whether `AdoptionDecisionDialog` could actually act on these two reads.
 *
 * `isSuccess` rather than "has data": TanStack Query keeps `.data` when a
 * REFETCH fails, so a cached success whose refetch settled as a retryable 503
 * still serves its last answer while reporting `status: "error"`. This dialog
 * has always failed closed on exactly that, and a surface deciding whether to
 * OFFER the control has to ask the same question or it offers a dead one.
 *
 * A PAUSED refetch is deliberately not a refusal: TanStack keeps the result
 * `success` while the retry is held, so a cached answer under a paused refetch
 * decides exactly as it did a moment earlier — which is what makes an offline
 * tab keep working rather than losing its controls.
 */
export function adoptionDecisionReady(
  adoptions: DecisionRead,
  catalog: DecisionRead,
): boolean {
  return adoptions.isSuccess && catalog.isSuccess;
}

export function AdoptionDecisionDialog({
  projectId,
  decision,
  onClose,
  unpinPresentation = "adoption",
}: {
  projectId: string;
  decision: AdoptionDecision;
  onClose: () => void;
  /** Project BG-set rows use the product-reference removal wording. */
  unpinPresentation?: "adoption" | "project-row";
}) {
  const current = useProjectAdoptionsQuery(projectId);
  const catalog = useLibraryAssetsQuery(true);
  // THE CARDINALITY POLICY, from the server that owns it (#634). Which pin a
  // replacement displaces is `families[].cardinality.adopted.max`, and this
  // dialog is the one surface that both states that consequence and performs
  // the write — so it is where the document has to be in hand. Same cache entry
  // every library surface already observes, so on a warm shell this settles
  // before the dialog paints.
  const registry = useLibraryPlacementRegistryQuery();
  const replace = useReplaceProjectAdoptionsMutation(projectId);
  // THE SECOND WRITER (#749). A Pathway family or variant does not go through
  // the whole-set replacement: its authored coordinate is refused there before
  // the write, and the registration that would make it reachable is the
  // server's to derive. `adoptionLane` below decides which of the two this
  // decision takes, from the family the SERVER states for the asset.
  const activate = useActivateProjectPathwaysMutation(projectId);
  // THE THIRD WRITER (#634): the Main-concern analogue. The project's single
  // Main concern is the runtime's opening evaluation, and the same
  // server-owned machinery registers its coordinate with the pin — or, on a
  // removal or base canon's own pin, returns the registration to the base
  // pack. One lane per family that registers; every other family replaces.
  const activateMainConcern = useActivateProjectMainConcernMutation(projectId);
  // THE FOURTH WRITER (#866's route, #867's control): the Closing conversation.
  // Its registration is a PAIR the pinned engine refuses to load with only one
  // half present, so — like the two above — the whole-set replacement cannot
  // adopt it and the Editor Service derives both enabler keys from the pin.
  const activateClosing =
    useActivateProjectClosingConversationMutation(projectId);
  // THE FIFTH WRITER (#858, wired by #914): the BG analogue, over the profile
  // and its domains TOGETHER. The two are refused separately and accepted
  // together — a profile with no domain at its variant and domains with no
  // profile behind them are both `background_profile_conflict` — so no order of
  // single-family writes reaches a coherent set, and the whole-set replacement
  // cannot select the variant that makes either reachable.
  const activateBackground = useActivateProjectBackgroundMutation(projectId);

  const busy =
    replace.isPending ||
    activate.isPending ||
    activateMainConcern.isPending ||
    activateClosing.isPending ||
    activateBackground.isPending;
  // The exported rule, read here rather than restated: the callers that decide
  // whether to OFFER a control call the same function, so the two halves cannot
  // drift into disagreeing about what this dialog can act on.
  //
  // The policy gate is a SECOND question and is deliberately not folded into it.
  // A removal must stay writable under a failed registry read — it is the
  // recovery path out of every refused state (#612) — so the surfaces that offer
  // controls keep asking only whether the SET can be described, and only a
  // `pin` waits here for the bound that decides what it displaces.
  const ready =
    adoptionDecisionReady(current, catalog) &&
    adoptionPolicyReady(registry, decision.mode);
  const assets: readonly LibraryAsset[] = catalog.data?.items ?? [];
  const subject = assets.find((asset) => asset.id === decision.assetId);
  const subjectName = subject?.name ?? "this asset";
  const pinning = decision.mode === "pin";
  // The same condition written out, because it is where the narrowing has to
  // happen: `adoptionDecisionReady` answers yes or no, and a boolean cannot tell
  // the type system that `current.data` is present. `pins` is therefore `null`
  // exactly when the set has not loaded, which every value below relies on.
  const pins = current.isSuccess && catalog.isSuccess ? current.data.items : null;
  const adoptionsVersion = current.isSuccess
    ? current.data.adoptionsVersion
    : null;
  // Which write this decision takes, from the family the server states for the
  // asset (#749, #634). Every lane reads the SAME impact: an activation body
  // is its own families' half of it, and the other half is what the server
  // preserves. For the Main concern that half is at most ONE pin — the
  // displacement the impact already applied is what keeps the served bound
  // (one adopted Main concern) true in the body, so the replaced pin is never
  // sent beside its replacement.
  const lane = pins !== null ? adoptionLane(decision, pins, assets) : "adoptions";
  const write =
    lane === "pathway-activation"
      ? activate
      : lane === "main-concern-activation"
        ? activateMainConcern
        : lane === "closing-activation"
          ? activateClosing
          : lane === "background-activation"
            ? activateBackground
            : replace;

  // THE IMPACT THE CATALOG CAN DESCRIBE: the pin that moves, and the pin a
  // served single-pin bound displaces. This is what the preflight is asked
  // about — the set the dialog would send if the server had nothing to add.
  const catalogImpact =
    pins !== null ? adoptionImpact(decision, pins, assets, registry.data) : null;
  const preflightItems =
    catalogImpact !== null && pins !== null && pinning
      ? lane === "adoptions"
        ? catalogImpact.items
        : activationImpact(catalogImpact, pins, assets, lane).items
      : null;
  // THE SERVER'S OWN VIEW, read before the first click. What no catalog read
  // can say — that an authored copy placed where base canon's asset already
  // lives REPLACES it rather than joining it — #632's preflight says by id
  // (`placement.ownership_conflict` + `ownershipConflicts`), through the same
  // lane the write would take. A write refusal carries the same shape, so the
  // plan below reads whichever the dialog has, latest first: a refused write
  // is newer evidence than the dry run that preceded it.
  const preflight = useAdoptionPreflightQuery(projectId, lane, preflightItems);
  const writeRefusal = write.isError
    ? adoptionPreflightRefusalFromError(write.error)
    : null;
  const plan: ReplacementPlan =
    pins !== null && pinning
      ? replacementPlan(
          decision,
          writeRefusal?.refusals ?? preflight.data?.items ?? [],
          pins,
        )
      : { kind: "none" };
  // THE IMPACT THIS DIALOG CONFIRMS AND SENDS: the catalog's, plus the owners
  // the server named. One computation feeds the sentence and the body, so the
  // replacement a clinician reads is the replacement the request performs —
  // and a placement the server never named is never dropped.
  const impact =
    catalogImpact !== null && pins !== null && plan.kind === "replace"
      ? adoptionImpact(
          decision,
          pins,
          assets,
          registry.data,
          plan.incumbents.map((pin) => pin.assetId),
        )
      : catalogImpact;
  const activation =
    lane !== "adoptions" && pins !== null && impact !== null
      ? activationImpact(impact, pins, assets, lane)
      : null;
  // WHAT THE PIN TAKES OVER FROM BASE CANON — the preflight's other fact.
  // `supersedes` names the seeded assets whose manifest-owned placement this
  // pin occupies, pinned or not: an authored copy of base canon's Summary
  // prompt at `chief_complaint` replaces the base pack's value there, while a
  // coordinate-family pin naming none adds a placement the base does not
  // ship. Read only for an accepted, authored subject whose family the served
  // registry places by coordinate; the sentence is the server's fact, never a
  // browser rule about which coordinates collide.
  const subjectDecision = preflight.data?.items.find(
    (item) => item.assetId === decision.assetId,
  );
  const supersedes =
    pinning && subjectDecision?.decision === "accept"
      ? (subjectDecision.supersedes ?? [])
      : null;
  const coordinatePlaced =
    subject !== undefined &&
    !subject.capabilities.seeded &&
    registry.data?.families.find((family) => family.assetType === subject.assetType)
      ?.placement.kind === "coordinate";

  function nameOf(assetId: string): string {
    return (
      assets.find((asset) => asset.id === assetId)?.name ?? "another asset"
    );
  }

  async function confirm() {
    if (!current.isSuccess || impact === null) {
      return;
    }
    try {
      if (activation !== null) {
        // The complete pin set OF THE LANE'S FAMILIES, under the token this
        // dialog LOADED. No coordinate, registration or mode travels: the
        // Editor Service derives the runtime registration from these exact
        // pins and keeps every other family's pin unchanged.
        const body = {
          expectedAdoptionsVersion: current.data.adoptionsVersion,
          items: [...activation.items],
        };
        if (lane === "main-concern-activation") {
          await activateMainConcern.mutateAsync(body);
        } else if (lane === "closing-activation") {
          await activateClosing.mutateAsync(body);
        } else if (lane === "background-activation") {
          await activateBackground.mutateAsync(body);
        } else {
          await activate.mutateAsync(body);
        }
      } else {
        await replace.mutateAsync({
          expectedAdoptionsVersion: current.data.adoptionsVersion,
          items: [...impact.items],
        });
      }
      onClose();
    } catch {
      // Rendered below from `write.error`; the dialog stays open so the
      // refusal is read beside the impact that motivated the attempt.
    }
  }

  const staleVersion = write.isError
    ? currentAdoptionsVersionFromError(write.error)
    : undefined;
  // The #632 write gate's typed evidence, rendered as ITEMS rather than as the
  // envelope's prose (#612): a refused replacement carries every refused pin
  // with the registry's own reason, and a clinician deciding what to fix needs
  // that list, not a summary of its first entry. A pin KEPT at its exact held
  // version is never re-decided by the gate — the contract's own clause — so a
  // pure removal cannot arrive here; this renders for adds and moves. The
  // activation lane refuses with the SAME envelope, so one rendering serves
  // both writers.
  //
  // ONE EXCEPTION, and it is the ownership rule's: a refusal the plan above
  // could turn into an unambiguous replacement is not rendered as a refusal
  // at all — the dialog states the replacement instead, and the next confirm
  // sends it. What stays a refusal is everything the plan could NOT resolve.
  const preflightRefusal = writeRefusal !== null && plan.kind !== "replace" ? writeRefusal : null;
  const failure = write.isError
    ? staleVersion
      ? `Another change to this project's adoptions landed first — the pin set is now ${staleVersion}. Nothing was written and no adoption was overwritten. The set below has been reloaded; decide again against it.`
      : preflightRefusal
        ? null
        : write.error instanceof ApiError
          ? write.error.message
          : "The Editor Service could not be reached, so nothing was written."
    : null;

  const projectRowRemoval = !pinning && unpinPresentation === "project-row";
  // The policy read specifically, and only where it is required. A removal is
  // never held on it, so this stays false there however the read settled.
  const policyMissing = pinning && !adoptionPolicyReady(registry, decision.mode);
  // The served bound, in the server's own words. Rendered only beside a real
  // displacement, so it explains the replacement the clinician is about to make
  // rather than announcing a rule nothing on screen depends on.
  const policySentence = singlePinSentence(registry.data);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        busy={busy}
        title={
          pinning
            ? impact?.from
              ? `Move this project's pin on ${subjectName}`
              : `Pin ${subjectName} into this project`
            : projectRowRemoval
              ? "Remove from project"
              : `Remove this project's pin on ${subjectName}`
        }
        description={
          pinning
            ? `This project will use ${subjectName} at the exact version below until the pin is moved.`
            : projectRowRemoval
              ? `${subjectName} will be removed from this project's configuration. The library asset itself stays available. You can re-adopt it later.`
              : `This project will stop using ${subjectName} altogether.`
        }
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!ready || busy || plan.kind === "unresolved"}
              onClick={() => void confirm()}
            >
              {pinning
                ? "Write this adoption"
                : projectRowRemoval
                  ? "Remove"
                  : "Remove this adoption"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          {current.isPending ||
          catalog.isPending ||
          (pinning && registry.isPending) ? (
            <LoadingState label="Loading this project's adoptions" rows={2} />
          ) : !ready || impact === null ? (
            // WHICH READ FAILED, because the two are different problems with the
            // same remedy and a clinician reading "adoptions could not be
            // loaded" beside a loaded pin set learns nothing. The retry re-reads
            // all three either way: they are one decision's inputs.
            <ErrorState
              title={
                policyMissing
                  ? "The placement policy could not be loaded"
                  : "This project's adoptions could not be loaded"
              }
              description={
                policyMissing
                  ? "The server owns which families a project may pin only once, so until that policy loads this surface cannot say which pin a replacement would displace — and it will not write one it cannot describe. Removing a pin does not depend on this read."
                  : "Until they load, the change is not offered — replacing a pin without seeing the set it belongs to is not a decision."
              }
              onRetry={() => {
                void current.refetch();
                void catalog.refetch();
                void registry.refetch();
              }}
            />
          ) : (
            <>
              {/* Criterion 4, in order: which pin moves and between which exact
                  versions, which pin it displaces, and what is written back
                  unchanged. Every number below comes from the same `impact`
                  value the confirm sends. */}
              {pinning ? (
                impact.from === null ? (
                  <p className="text-foreground">
                    This project does not pin {subjectName} today. This adds it
                    at <strong>{impact.to}</strong>.
                  </p>
                ) : (
                  <p className="text-foreground">
                    This moves this project&apos;s pin on {subjectName} from{" "}
                    <strong>{impact.from}</strong> to{" "}
                    <strong>{impact.to}</strong>.
                  </p>
                )
              ) : (
                <p className="text-foreground">
                  This project pins {subjectName} at{" "}
                  <strong>{impact.from ?? "—"}</strong>. Removing the pin leaves
                  it with no adoption of that asset.
                </p>
              )}

              {impact.displaced ? (
                <>
                  <p className="text-foreground">
                    This project currently pins{" "}
                    <strong>{nameOf(impact.displaced.assetId)}</strong> at{" "}
                    <strong>{impact.displaced.version}</strong>. Confirming
                    replaces that pin — it is not added alongside it.
                  </p>
                  {policySentence ? (
                    <p className="text-muted-foreground">{policySentence}</p>
                  ) : null}
                </>
              ) : null}

              {impact.replacedOwners.length > 0 ? (
                // THE REPLACEMENT THE SERVER DECIDED: the incoming pin owns
                // the same runtime placement as these held pins, so writing
                // it means writing the set WITHOUT them. Stated by name and
                // exact version before the write, never applied silently.
                <>
                  <p className="text-foreground">
                    Pinning <strong>{subjectName}</strong> will replace{" "}
                    {impact.replacedOwners.map((pin, index) => (
                      <span key={pin.assetId}>
                        {index > 0
                          ? index === impact.replacedOwners.length - 1
                            ? " and "
                            : ", "
                          : null}
                        <strong>{nameOf(pin.assetId)}</strong>{" "}
                        <strong>{pin.version}</strong>
                      </span>
                    ))}{" "}
                    at the same runtime placement. Confirming removes{" "}
                    {impact.replacedOwners.length === 1 ? "that pin" : "those pins"}{" "}
                    from this project — {impact.replacedOwners.length === 1 ? "it is" : "they are"}{" "}
                    not kept alongside the new one.
                  </p>
                  <p className="text-muted-foreground">
                    The Editor Service decided this replacement: one runtime
                    placement has one adopted owner, and it names which pin
                    holds it today. Nothing has been written yet.
                  </p>
                </>
              ) : null}

              {supersedes !== null && supersedes.length > 0 ? (
                // The pin occupies the base pack's own placement. When the
                // seeded row is also PINNED the replacement sentence above
                // already names it; this states the other half — the value
                // the project inherits from the base pack today goes.
                <p className="text-foreground">
                  This pin takes over the base pack&apos;s own placement of{" "}
                  {supersedes.map((assetId, index) => (
                    <span key={assetId}>
                      {index > 0 ? (index === supersedes.length - 1 ? " and " : ", ") : null}
                      <strong>{nameOf(assetId)}</strong>
                    </span>
                  ))}
                  : adopting it replaces the base pack&apos;s content at that
                  runtime placement, which this project inherits today. It does
                  not add a second placement beside it.
                </p>
              ) : supersedes !== null && coordinatePlaced ? (
                <p className="text-foreground">
                  This pin adds a runtime placement the base pack does not
                  ship. It replaces nothing: every base-pack value stays as it
                  is, and this project gains one more.
                </p>
              ) : null}

              {plan.kind === "unresolved" && preflightRefusal === null ? (
                // THE SERVER COULD NOT HAND BACK AN UNAMBIGUOUS PLAN: an owner
                // it named is not a pin this project holds, or another item
                // was refused beside it. Refuse safely — the confirm stays
                // disabled — and name what has to be resolved by hand.
                <Notice role="alert" tone="destructive">
                  <p>
                    The Editor Service refused this pin before anything was
                    written: another adopted asset already owns the same
                    runtime placement, and no single replacement could be
                    derived from this project&apos;s pins. Resolve these
                    adoptions first, then decide again.
                  </p>
                  {plan.conflicts.length > 0 ? (
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {plan.conflicts.map((assetId) => (
                        <li key={assetId}>{nameOf(assetId)}</li>
                      ))}
                    </ul>
                  ) : null}
                </Notice>
              ) : null}

              {activation !== null ? (
                // THE ACTIVATION LANE'S OWN CONSEQUENCE (#749, #634, #867): the
                // runtime registration is written by the Editor Service from
                // these exact pins, and the other families' pins are preserved
                // server-side rather than re-sent. Both facts are stated
                // because both are what the clinician is confirming. The
                // Main concern's sentence is the family's own: its single pin
                // IS the opening evaluation, so a seeded pin or a removal
                // returns that registration to the base pack rather than
                // leaving the runtime without one.
                <p className="text-foreground">
                  {lane === "main-concern-activation"
                    ? pinning
                      ? subject?.capabilities.seeded
                        ? `This is the base pack's own Main concern: the Editor Service returns this project's opening-evaluation registration to the base pack as part of this write — nothing is typed here.`
                        : `The Editor Service registers this Main concern's runtime coordinate in this project's configuration as its single opening evaluation, as part of this write — nothing is typed here.`
                      : `The Editor Service removes this Main concern's runtime registration with the pin; this project's opening evaluation returns to the base pack's own Main concern.`
                    : lane === "closing-activation"
                      ? // THE CLOSING LANE'S OWN SENTENCE (#867). The two enabler
                        // keys move together because the pinned engine refuses a
                        // pack carrying only one of them, and a removal returns
                        // the project to the base pack's own closing
                        // configuration — which for the base pack is no closing
                        // node at all, so the session ends after the final
                        // tracks. Neither wording claims a diagnostic selection:
                        // this family takes no part in Always/Dynamic.
                        pinning
                        ? `The Editor Service wires this project's closing node as part of this write, enabling the closing conversation after the final tracks — nothing is typed here.`
                        : `The Editor Service unwires this project's closing node with the pin; the project returns to the base pack's own closing configuration, and sessions complete straight after the final tracks.`
                    : lane === "background-activation"
                      ? // THE BG LANE'S CONSEQUENCE (#914). The registration is
                        // a fact about the profile and the domains TOGETHER —
                        // which interview profile this project runs, and which
                        // background areas it asks in which order — so both
                        // halves are named whichever one is moving, and a write
                        // that empties the set returns both to the base pack.
                        pinning
                        ? `The Editor Service selects this project's interview profile and background-area order from its complete set of background pins as part of this write — nothing is typed here.`
                        : `The Editor Service removes this pin and re-derives this project's interview profile and background-area order from the pins that remain; with none left, both return to the base pack's.`
                      : pinning
                        ? `The Editor Service registers this Pathway's runtime coordinate in this project's configuration as part of this write and decides its Always/Dynamic selection — nothing is typed here.`
                        : `The Editor Service removes this Pathway's runtime registration with the pin, unless another active Pathway in this project still needs it.`}
                </p>
              ) : null}

              <p className="text-muted-foreground">
                {activation !== null
                  ? activation.preserved.length === 0
                    ? "No other adoption in this project changes."
                    : `${activation.preserved.length} other ${
                        activation.preserved.length === 1
                          ? "adoption"
                          : "adoptions"
                      } in this project ${
                        activation.preserved.length === 1 ? "is" : "are"
                      } kept unchanged by the Editor Service.`
                  : impact.unchanged.length === 0
                    ? "No other adoption in this project changes."
                    : `${impact.unchanged.length} other ${
                        impact.unchanged.length === 1 ? "adoption" : "adoptions"
                      } in this project ${
                        impact.unchanged.length === 1 ? "is" : "are"
                      } written back unchanged.`}
              </p>

              {pinning && preflightItems !== null && preflight.isPending ? (
                <p className="text-xs text-muted-foreground">
                  Checking this pin against the project&apos;s other adoptions…
                </p>
              ) : null}

              <p className="text-xs text-muted-foreground">
                Pin set {adoptionsVersion} · a pin names an immutable version,
                so a later publish never moves it.
              </p>
            </>
          )}

          {failure ? (
            <Notice role="alert" tone="destructive">
              {failure}
            </Notice>
          ) : null}

          {preflightRefusal ? (
            <Notice role="alert" tone="destructive">
              <p>
                {activation !== null
                  ? `The placement registry (${preflightRefusal.registryVersion}) refused this activation before anything was written. Neither the pin set nor the runtime registration changed; fix the items below and confirm again.`
                  : `The placement registry (${preflightRefusal.registryVersion}) refused this replacement before anything was written. The pin set is unchanged; fix the items below and confirm again.`}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {preflightRefusal.refusals.map((item) => (
                  <li key={item.index}>
                    <span className="font-medium">{nameOf(item.assetId)}</span>{" "}
                    at {item.version} —{" "}
                    {/* The registry's vocabulary widens with policy versions,
                        so an unknown member renders as its raw code rather
                        than crashing the one surface that explains it. */}
                    {item.reason
                      ? (PLACEMENT_REFUSAL_LABEL[item.reason] ?? item.reason)
                      : "refused by the placement registry"}
                    {item.ownershipConflicts && item.ownershipConflicts.length > 0
                      ? ` — owned today by ${item.ownershipConflicts
                          .map((assetId) => nameOf(assetId))
                          .join(", ")}; remove that adoption first, or decide again once the set has reloaded`
                      : null}
                  </li>
                ))}
              </ul>
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
